"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { Trash2, ShieldAlert, Ban, RotateCcw } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/components/ui/toast";
import ModerationDialog from "@/components/admin/ModerationDialog";

export default function AdminProfileToolbar({ personId, displayName }: { personId: string | number; displayName: string }) {
  const router = useRouter();
  const { data: session } = useSession();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);            // delete confirm
  const [reinstateOpen, setReinstateOpen] = React.useState(false);
  const [warnOpen, setWarnOpen] = React.useState(false);
  const [suspendOpen, setSuspendOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [suspended, setSuspended] = React.useState<boolean | null>(null);
  const [busy, setBusy] = React.useState(false);

  const isAdmin = Boolean(
    (session as any)?.user?.role === "admin" ||
    (session as any)?.user?.isAdmin === true
  );

  // Moderation endpoints are keyed by the numeric People id.
  const numericId = typeof personId === "number"
    ? personId
    : (/^\d+$/.test(String(personId)) ? Number(personId) : null);

  // Load current suspended state so we can show Suspend vs Reinstate.
  React.useEffect(() => {
    if (!isAdmin || numericId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/proxy/api/admin/people/${numericId}`, { cache: "no-store" });
        if (!res.ok) return;
        const j = await res.json().catch(() => ({}));
        if (!cancelled) setSuspended(Boolean(j?.suspended));
      } catch {
        /* non-fatal — buttons still work, we just default to Suspend */
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, numericId]);

  if (!isAdmin) return null;

  async function post(action: string, body?: any): Promise<boolean> {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`/api/proxy/api/admin/people/${numericId}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        setError(b?.detail || b?.error || `Failed (${res.status})`);
        return false;
      }
      return true;
    } catch (e: any) {
      setError(e?.message || "Request failed");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleWarn(message: string) {
    if (await post("warn", { message })) {
      toast({ title: "Warning sent", description: `${displayName} was warned.`, variant: "success" });
    }
  }

  async function handleSuspend(reason: string) {
    if (await post("suspend", { reason })) {
      setSuspended(true);
      toast({ title: "Suspended", description: `${displayName} suspended — public profile hidden.`, variant: "success" });
    }
  }

  async function handleReinstate() {
    if (await post("unsuspend")) {
      setSuspended(false);
      setReinstateOpen(false);
      toast({ title: "Reinstated", description: `${displayName} reinstated.`, variant: "success" });
    }
  }

  async function handleDelete() {
    setError(null);
    const res = await fetch(`/api/proxy/api/admin/people/${personId}`, {
      method: "DELETE",
      headers: { "X-Confirm-Word": "DELETE" },
    });
    if (res.status === 204 || res.ok) {
      router.push("/search");
    } else {
      const body = await res.json().catch(() => ({}));
      setError(body?.detail || body?.error || `Delete failed (${res.status})`);
    }
  }

  const btn = "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium text-white transition-colors disabled:opacity-50";

  return (
    <>
      <div className="bg-red-950/90 border border-red-800 rounded-lg px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs font-medium text-red-200 uppercase tracking-wide">
          Admin{suspended ? " · suspended" : ""}
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          {error && <span className="text-xs text-red-300">{error}</span>}
          {numericId != null && (
            <>
              <button
                onClick={() => setWarnOpen(true)}
                disabled={busy}
                className={btn + " bg-amber-600 hover:bg-amber-700"}
                title="Email + in-app notice asking them to fix flagged content"
              >
                <ShieldAlert className="h-4 w-4" />
                Warn
              </button>
              {suspended ? (
                <button
                  onClick={() => setReinstateOpen(true)}
                  disabled={busy}
                  className={btn + " bg-zinc-600 hover:bg-zinc-700"}
                  title="Make their profile + content visible again"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reinstate
                </button>
              ) : (
                <button
                  onClick={() => setSuspendOpen(true)}
                  disabled={busy}
                  className={btn + " bg-orange-600 hover:bg-orange-700"}
                  title="Hide their public profile + content (they can still log in to fix it)"
                >
                  <Ban className="h-4 w-4" />
                  Suspend
                </button>
              )}
            </>
          )}
          <button onClick={() => setOpen(true)} className={btn + " bg-red-600 hover:bg-red-700"}>
            <Trash2 className="h-4 w-4" />
            Delete Profile
          </button>
        </div>
      </div>

      <ModerationDialog
        open={warnOpen}
        onOpenChange={setWarnOpen}
        title={`Warn ${displayName}`}
        description="Send a notice asking them to fix flagged content. Their profile stays visible; they fix it themselves."
        label="Message to the user"
        placeholder="e.g. Please remove the escort/solicitation language from your bio — it violates our terms."
        confirmLabel="Send warning"
        confirmClass="bg-amber-600 hover:bg-amber-700"
        onConfirm={handleWarn}
      />

      <ModerationDialog
        open={suspendOpen}
        onOpenChange={setSuspendOpen}
        title={`Suspend ${displayName}`}
        description="Hides their public profile + content. They can still sign in to fix it and be reviewed for reinstatement. Your reason is included in the email."
        label="Reason (sent to the user)"
        placeholder="e.g. Your bio solicits escort services, which is not allowed. Remove it to be reinstated."
        confirmLabel="Suspend account"
        confirmClass="bg-orange-600 hover:bg-orange-700"
        onConfirm={handleSuspend}
      />

      <ConfirmDialog
        open={reinstateOpen}
        onOpenChange={setReinstateOpen}
        title={`Reinstate ${displayName}?`}
        message="Their profile and content become visible again."
        confirmLabel="Reinstate"
        onConfirm={handleReinstate}
      />

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`Delete ${displayName}?`}
        message={`This will permanently delete this profile and all associated content (images, videos, posts, messages). This cannot be undone.`}
        confirmLabel="Delete"
        confirmType="danger"
        typedPhrase="DELETE"
        onConfirm={handleDelete}
      />
    </>
  );
}
